<template>
    <rkm-modal
        extra-large
        persistent
        ref="RkmModal"
        title="Declaração de comparecimento"
    >
        <rkm-form
            v-if="model"
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input
                        :value="form.paciente.nome"
                        disabled
                        label="Nome do paciente"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :value="form.paciente.cpf"
                        disabled
                        label="CPF"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :value="form.paciente.cns"
                        disabled
                        label="CNS"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="form.turnos"
                        @change="handleTurnoChange"
                        default-option-text="Horário personalizado"
                        label="Turno"
                        show-default-option
                        text-field="nome"
                        v-model="model.turnoId"
                        value-field="id"
                    />
                </rkm-col>

                <template v-if="model.turnoId === null">
                    <rkm-col cols="3">
                        <rkm-form-time-picker
                            :validations="validations.entrada"
                            label="Hora da entrada"
                            v-model="model.entrada"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-time-picker
                            :validations="validations.saida"
                            label="Hora da saída"
                            v-model="model.saida"
                        />
                    </rkm-col>
                </template>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        :validations="validations.nomeAcompanhante"
                        label="Nome do acompanhante"
                        uppercase
                        v-model="model.nomeAcompanhante"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.motivoComparecimento"
                        label="Motivo do comparecimento"
                        rows="4"
                        v-model="model.motivoComparecimento"
                        maxlength="1500"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <template v-slot:footer>
            <rkm-button @click="cancel">
                Cancelar
            </rkm-button>

            <rkm-button
                @click="store"
                color="primary"
            >
                Salvar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Mixins.
import {error, file, formats, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        validations() {
            return {
                entrada: {
                    required: true,
                    max_hour: this.model.saida ? this.formatTime(this.model.saida) : null,
                },
                saida: {
                    required: true,
                    min_hour: this.model.entrada ? this.formatTime(this.model.entrada) : null,
                },
                nomeAcompanhante: {max: 150},
                motivoComparecimento: {max: 1500},
            }
        },
    },
    created() {
        this.repositories = {
            gerenciamentoDiario: this.getConectaSusRepository('ambulatorio.gerenciamentoDiario'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
        }
    },
    data: () => ({
        allowsToPrint: false,
        atendimentoId: null,
        form: null,
        model: null,
        preAtendimentoId: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(atendimentoId, params = {}) {
            this.atendimentoId = atendimentoId
            this.preAtendimentoId = params.preAtendimentoId || null
            this.allowsToPrint = params.allowsToPrint || false

            this.create()

            this.$refs.RkmModal.show()
        },
        create() {
            this.reset()

            this.setLoading()

            const request = {
                origem: 'declaracao-comparecimento',
                atendimentoId: this.atendimentoId,
            }

            this.repositories.gerenciamentoDiario.create(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleTurnoChange() {
            this.model.entrada = null
            this.model.saida = null
        },
        store() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.setLoading()

                    const request = {
                        origem: 'declaracao-comparecimento',
                        atendimentoId: this.atendimentoId,
                        preAtendimentoId: this.preAtendimentoId,
                        turnoId: this.model.turnoId,
                        entrada: this.model.entrada ? this.formatTime(this.model.entrada) : null,
                        saida: this.model.saida ? this.formatTime(this.model.saida) : null,
                        nomeAcompanhante: this.model.nomeAcompanhante,
                        motivoComparecimento: this.model.motivoComparecimento,
                    }

                    this.repositories.gerenciamentoDiario.store(this.camelObjectKeysToSnakeRecursive(request))
                        .then(() => {
                            this.showSaveNotification()

                            if (this.allowsToPrint) {
                                this.print(this.atendimentoId)
                            }

                            this.$emit('saved', this.atendimentoId)
                            this.close()
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        cancel() {
            this.$emit('canceled', this.atendimentoId)
            this.close()
        },
        close() {
            this.$refs.RkmModal.hide()
        },
        print(atendimentoId) {
            this.setLoading()

            this.repositories.impressao.declaracaoComparecimento(atendimentoId)
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form = null
            this.model = null
        },
    },
    mixins: [error, file, formats, repository, stringCase],
}
</script>